import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut, Save, Plus, Trash2, Edit2, Check, X, 
  ArrowUp, ArrowDown, LayoutDashboard, List as ListIcon, 
  Settings, Users, Bell, Trophy, Image, Type,
  Menu, ChevronLeft, Upload
} from 'lucide-react';
import API_BASE_URL from '../../apiConfig';

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sponsors, setSponsors] = useState([]);
  const [winners, setWinners] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [popupSites, setPopupSites] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = async () => {
    try {
      const [sponsorsRes, winnersRes, notifRes, popupSitesRes, settingsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/sponsor_ops.php?action=list_all&table=sponsors`),
        fetch(`${API_BASE_URL}/sponsor_ops.php?action=list_all&table=winners`),
        fetch(`${API_BASE_URL}/sponsor_ops.php?action=list_all&table=notifications`),
        fetch(`${API_BASE_URL}/sponsor_ops.php?action=list_all&table=popup_sites`),
        fetch(`${API_BASE_URL}/get_data.php`)
      ]);

      const sponsorsData = await sponsorsRes.json();
      const winnersData = await winnersRes.json();
      const notifData = await notifRes.json();
      const popupSitesData = await popupSitesRes.json();
      const settingsData = await settingsRes.json();

      setSponsors(sponsorsData.sort((a, b) => a.sort_order - b.sort_order));
      setWinners(winnersData.sort((a, b) => a.rank_num - b.rank_num));
      setNotifications(notifData);
      setPopupSites(popupSitesData.sort((a, b) => a.sort_order - b.sort_order));
      
      const { logoCards, bettingSites, winners: w, notifications: n, popupSites: p, ...generalSettings } = settingsData;
      setSettings(generalSettings);
      
      setLoading(false);
    } catch (error) {
      console.error("Veri çekme hatası:", error);
      showToast("Veriler yüklenirken hata oluştu", "error");
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleSaveSettings = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/save_data.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      const data = await res.json();
      if (data.success) {
        showToast('Ayarlar başarıyla kaydedildi');
      } else {
        showToast(data.message, 'error');
      }
    } catch (error) {
      showToast('Kaydetme hatası', 'error');
    }
  };

  const handleItemSave = async (itemData, table) => {
    const action = itemData.id ? 'update' : 'add';
    
    if (!itemData.id) {
        if (table === 'sponsors') {
            const sameType = sponsors.filter(s => s.type === itemData.type);
            const maxOrder = sameType.length > 0 ? Math.max(...sameType.map(s => s.sort_order)) : -1;
            itemData.sort_order = maxOrder + 1;
        } else if (table === 'winners') {
            const maxRank = winners.length > 0 ? Math.max(...winners.map(w => w.rank_num)) : 0;
            itemData.rank_num = maxRank + 1;
        } else if (table === 'popup_sites') {
            const maxOrder = popupSites.length > 0 ? Math.max(...popupSites.map(s => s.sort_order)) : -1;
            itemData.sort_order = maxOrder + 1;
        }
    }

    try {
      const res = await fetch(`${API_BASE_URL}/sponsor_ops.php?action=${action}&table=${table}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemData)
      });
      const data = await res.json();
      if (data.success) {
        setEditModal(null);
        fetchData();
        showToast('İşlem başarılı');
      } else {
        showToast(data.error || 'İşlem başarısız', 'error');
      }
    } catch (error) {
      showToast('Bir hata oluştu', 'error');
    }
  };

  const handleDeleteItem = async (id, table) => {
    if (!window.confirm('Bu öğeyi silmek istediğinize emin misiniz?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/sponsor_ops.php?action=delete&table=${table}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      const data = await res.json();
      if (data.success) {
        fetchData();
        showToast('Öğe silindi');
      }
    } catch (error) {
      showToast('Silme hatası', 'error');
    }
  };
  
  const handleToggleActiveSponsor = async (sponsor) => {
      const updatedSponsor = { ...sponsor, is_active: sponsor.is_active == 1 ? 0 : 1 };
      handleItemSave(updatedSponsor, 'sponsors');
  };

  const handleToggleActivePopupSite = async (site) => {
      const updatedSite = { ...site, is_active: site.is_active == 1 ? 0 : 1 };
      handleItemSave(updatedSite, 'popup_sites');
  };

  const handleMove = async (index, direction, type, table) => {
    let list = [];
    if (table === 'sponsors') list = sponsors.filter(s => s.type === type);
    else if (table === 'winners') list = [...winners];
    else if (table === 'popup_sites') list = [...popupSites];
    else return;

    const itemIndex = list.findIndex((_, i) => i === index);
    if (direction === 'up' && itemIndex === 0) return;
    if (direction === 'down' && itemIndex === list.length - 1) return;

    const currentItem = list[itemIndex];
    const targetItem = direction === 'up' ? list[itemIndex - 1] : list[itemIndex + 1];
    
    if (table === 'sponsors' || table === 'popup_sites') {
        const temp = currentItem.sort_order;
        currentItem.sort_order = targetItem.sort_order;
        targetItem.sort_order = temp;
    } else if (table === 'winners') {
        const temp = currentItem.rank_num;
        currentItem.rank_num = targetItem.rank_num;
        targetItem.rank_num = temp;
    }

    const itemsToUpdate = [
        { id: currentItem.id, order: table === 'winners' ? currentItem.rank_num : currentItem.sort_order },
        { id: targetItem.id, order: table === 'winners' ? targetItem.rank_num : targetItem.sort_order }
    ];

    try {
        await fetch(`${API_BASE_URL}/sponsor_ops.php?action=reorder&table=${table}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: itemsToUpdate })
        });
        fetchData();
    } catch(error) {
        console.error("Sıralama hatası:", error);
    }
  };

  // Resim Yükleme Fonksiyonu
  const handleFileUpload = async (e, fieldName, setFormData) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
        showToast('Dosya yükleniyor...', 'info');
        const res = await fetch(`${API_BASE_URL}/upload.php`, {
            method: 'POST',
            body: formData
        });
        const data = await res.json();
        
        if (data.success) {
            setFormData(prev => ({ ...prev, [fieldName]: data.url }));
            showToast('Dosya yüklendi');
        } else {
            showToast(data.message, 'error');
        }
    } catch (error) {
        console.error("Upload hatası:", error);
        showToast('Yükleme hatası', 'error');
    }
  };

  // --- Bileşenler ---

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg hover:border-gray-600 transition-all">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm mb-1">{title}</p>
          <h3 className="text-2xl font-bold text-white">{value}</h3>
        </div>
        <div className={`p-3 rounded-lg bg-${color}-500/10 text-${color}-500`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );

  const Toast = ({ message, type }) => (
    <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-xl text-white animate-slideIn ${type === 'error' ? 'bg-red-600' : type === 'info' ? 'bg-blue-600' : 'bg-green-600'}`}>
      {message}
    </div>
  );

  const ImageInput = ({ label, value, onChange, onUpload }) => {
    const fileInputRef = useRef(null);

    return (
        <div>
            <label className="block text-sm text-gray-400 mb-1">{label}</label>
            <div className="flex gap-2">
                <input 
                    value={value || ''} 
                    onChange={onChange} 
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2.5 text-white focus:border-blue-500 focus:outline-none" 
                    placeholder="https://..."
                />
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    className="hidden" 
                    onChange={onUpload}
                    accept="image/*"
                />
                <button 
                    onClick={() => fileInputRef.current.click()} 
                    className="bg-gray-700 hover:bg-gray-600 text-white p-2.5 rounded-lg border border-gray-600 transition-colors"
                    title="Dosya Yükle"
                >
                    <Upload size={20} />
                </button>
                {value && <img src={value} alt="Preview" className="w-10 h-10 object-contain bg-gray-800 rounded border border-gray-700" />}
            </div>
        </div>
    );
  };

  const Modal = ({ item, onClose, onSave, table, defaultType }) => {
    const [formData, setFormData] = useState(item || {});

    useEffect(() => {
        if (!item) {
            if (table === 'sponsors') {
                setFormData({ name: '', image: '', url: '', type: defaultType || 'list', title: '', badge: '', tag: '', background: '', is_active: 1 });
            } else if (table === 'winners' || table === 'notifications') {
                setFormData({ user: '', site: '', amount: '' });
            } else if (table === 'popup_sites') {
                setFormData({ name: '', image: '', url: '', is_active: 1 });
            }
        }
    }, [item, table, defaultType]);

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-2xl border border-gray-700 shadow-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6 border-b border-gray-800 pb-4">
            <h3 className="text-xl font-bold text-white">
                {item ? 'Düzenle' : 'Yeni Ekle'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={24}/></button>
          </div>
          
          <div className="space-y-4">
            {(table === 'sponsors' || table === 'popup_sites') && (
                <>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">İsim</label>
                            <input name="name" value={formData.name || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2.5 text-white focus:border-blue-500 focus:outline-none" />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">URL</label>
                            <input name="url" value={formData.url || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2.5 text-white focus:border-blue-500 focus:outline-none" />
                        </div>
                    </div>
                    
                    <ImageInput 
                        label="Görsel URL" 
                        value={formData.image} 
                        onChange={handleChange} 
                        onUpload={(e) => handleFileUpload(e, 'image', setFormData)}
                    />

                    {/* Sponsor specific fields */}
                    {table === 'sponsors' && formData.type === 'slider' && (
                        <>
                            <ImageInput 
                                label="Arkaplan URL" 
                                value={formData.background} 
                                onChange={handleChange} 
                                onUpload={(e) => handleFileUpload(e, 'background', setFormData)}
                            />
                            
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Başlık</label>
                                <input name="title" value={formData.title || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2.5 text-white focus:border-blue-500 focus:outline-none" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Rozet</label>
                                    <input name="badge" value={formData.badge || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2.5 text-white focus:border-blue-500 focus:outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Etiket</label>
                                    <input name="tag" value={formData.tag || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2.5 text-white focus:border-blue-500 focus:outline-none" />
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}

            {(table === 'winners' || table === 'notifications') && (
                <div className="grid grid-cols-1 gap-4">
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Kullanıcı Adı</label>
                        <input name="user" value={formData.user || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2.5 text-white focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Site Adı</label>
                        <input name="site" value={formData.site || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2.5 text-white focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Miktar</label>
                        <input name="amount" value={formData.amount || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2.5 text-white focus:border-blue-500 focus:outline-none" />
                    </div>
                </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-800">
            <button onClick={onClose} className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors">İptal</button>
            <button onClick={() => onSave(formData, table)} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-white shadow-lg shadow-blue-500/20 transition-all flex items-center gap-2">
              <Save size={18} /> Kaydet
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Settings Upload Handlers
  const handleSettingsUpload = async (e, field) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);

      try {
          showToast('Yükleniyor...', 'info');
          const res = await fetch(`${API_BASE_URL}/upload.php`, {
              method: 'POST',
              body: formData
          });
          const data = await res.json();
          if (data.success) {
              setSettings(prev => ({ ...prev, [field]: data.url }));
              showToast('Yüklendi');
          } else {
              showToast(data.message, 'error');
          }
      } catch (error) {
          showToast('Hata', 'error');
      }
  };
  
  const handlePopupUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
          showToast('Yükleniyor...', 'info');
          const res = await fetch(`${API_BASE_URL}/upload.php`, {
              method: 'POST',
              body: formData
          });
          const data = await res.json();
          if (data.success) {
              setSettings(prev => ({ ...prev, popup: { ...prev.popup, image: data.url } }));
              showToast('Yüklendi');
          } else {
              showToast(data.message, 'error');
          }
      } catch (error) {
          showToast('Hata', 'error');
      }
  }


  if (loading) return <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">Yükleniyor...</div>;

  return (
    <div className="flex min-h-screen bg-gray-950 text-white font-sans selection:bg-blue-500 selection:text-white">
      {/* Toast Notification */}
      {toast && <Toast message={toast.message} type={toast.type} />}

      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 border-r border-gray-800 transition-all duration-300 flex flex-col fixed h-full z-20`}>
        <div className="p-4 flex items-center justify-between border-b border-gray-800">
          {sidebarOpen ? <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Admin Panel</h1> : <div className="w-8 h-8 bg-blue-600 rounded-lg"></div>}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white">
            {sidebarOpen ? <ChevronLeft /> : <Menu />}
          </button>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          {[
            { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
            { id: 'sponsors', icon: Users, label: 'Sponsorlar' },
            { id: 'content', icon: Trophy, label: 'İçerik Yönetimi' },
            { id: 'settings', icon: Settings, label: 'Ayarlar' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all duration-200 ${
                activeTab === item.id 
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' 
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <item.icon size={20} />
              {sidebarOpen && <span className="font-medium">{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-800">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 p-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors">
            <LogOut size={20} />
            {sidebarOpen && <span>Çıkış Yap</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} p-8 transition-all duration-300`}>
        
        {/* Header */}
        <header className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-white capitalize">
                {activeTab === 'dashboard' ? 'Genel Bakış' : 
                 activeTab === 'sponsors' ? 'Sponsor Yönetimi' :
                 activeTab === 'content' ? 'İçerik Yönetimi' : 'Site Ayarları'}
            </h2>
            <p className="text-gray-400 text-sm mt-1">Hoş geldiniz, Admin</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">A</div>
          </div>
        </header>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard title="Toplam Sponsor" value={sponsors.length} icon={Users} color="blue" />
                <StatCard title="Aktif VIP" value={sponsors.filter(s => s.type === 'slider' && s.is_active == 1).length} icon={Trophy} color="yellow" />
                <StatCard title="Aktif Liste" value={sponsors.filter(s => s.type === 'list' && s.is_active == 1).length} icon={ListIcon} color="green" />
                <StatCard title="Toplam Bildirim" value={notifications.length} icon={Bell} color="purple" />
            </div>
        )}

        {/* Sponsors Tab */}
        {activeTab === 'sponsors' && (
            <div className="space-y-8">
                {/* VIP List */}
                <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
                    <div className="p-6 border-b border-gray-700 flex justify-between items-center bg-gray-800/50">
                        <div>
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <span className="text-yellow-500">👑</span> VIP Sponsorlar
                            </h3>
                            <p className="text-sm text-gray-400">Slider alanında gösterilen premium sponsorlar</p>
                        </div>
                        <button onClick={() => setEditModal({ table: 'sponsors', defaultType: 'slider' })} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
                            <Plus size={18} /> Ekle
                        </button>
                    </div>
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {sponsors.filter(s => s.type === 'slider').map((sponsor, idx) => (
                            <div key={sponsor.id} className="bg-gray-900/50 border border-gray-700 rounded-xl overflow-hidden hover:border-gray-600 transition-colors group">
                                <div className="relative h-40 bg-gray-900 p-4 flex items-center justify-center">
                                    <img src={sponsor.image} alt={sponsor.name} className="max-w-full max-h-full object-contain p-2" />
                                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleMove(idx, 'up', 'slider', 'sponsors')} className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-white"><ArrowUp size={14}/></button>
                                        <button onClick={() => handleMove(idx, 'down', 'slider', 'sponsors')} className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-white"><ArrowDown size={14}/></button>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="font-bold text-white truncate pr-2">{sponsor.name}</h4>
                                        <button onClick={() => handleToggleActiveSponsor(sponsor)} className={`p-1 rounded ${sponsor.is_active == 1 ? 'text-green-400 bg-green-400/10' : 'text-gray-500 bg-gray-700'}`}>
                                            {sponsor.is_active == 1 ? <Check size={14}/> : <X size={14}/>}
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 truncate mb-4">{sponsor.url}</p>
                                    <div className="flex gap-2">
                                        <button onClick={() => setEditModal({ ...sponsor, table: 'sponsors' })} className="flex-1 py-2 bg-blue-600/10 text-blue-400 hover:bg-blue-600/20 rounded-lg text-sm font-medium transition-colors">Düzenle</button>
                                        <button onClick={() => handleDeleteItem(sponsor.id, 'sponsors')} className="px-3 py-2 bg-red-600/10 text-red-400 hover:bg-red-600/20 rounded-lg transition-colors"><Trash2 size={16}/></button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Normal List */}
                <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
                    <div className="p-6 border-b border-gray-700 flex justify-between items-center">
                        <div>
                            <h3 className="text-lg font-bold text-white">Liste Sponsorları</h3>
                            <p className="text-sm text-gray-400">Liste görünümünde yer alan standart sponsorlar</p>
                        </div>
                        <button onClick={() => setEditModal({ table: 'sponsors', defaultType: 'list' })} className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
                            <Plus size={18} /> Ekle
                        </button>
                    </div>
                    <div className="p-6 space-y-3">
                        {sponsors.filter(s => s.type === 'list').map((sponsor, idx) => (
                            <div key={sponsor.id} className="flex items-center justify-between p-4 bg-gray-900/50 border border-gray-700 rounded-xl hover:border-gray-500 transition-colors">
                                <div className="flex items-center gap-4">
                                    <div className="flex flex-col gap-1">
                                        <button onClick={() => handleMove(idx, 'up', 'list', 'sponsors')} className="text-gray-500 hover:text-white"><ArrowUp size={16}/></button>
                                        <button onClick={() => handleMove(idx, 'down', 'list', 'sponsors')} className="text-gray-500 hover:text-white"><ArrowDown size={16}/></button>
                                    </div>
                                    <div className="w-12 h-12 bg-gray-800 rounded-lg p-2 flex items-center justify-center border border-gray-700">
                                        <img src={sponsor.image} alt="" className="max-w-full max-h-full" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white">{sponsor.name}</h4>
                                        <a href={sponsor.url} target="_blank" rel="noreferrer" className="text-xs text-blue-400 hover:underline">{sponsor.url}</a>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${sponsor.is_active == 1 ? 'bg-green-500/10 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
                                        {sponsor.is_active == 1 ? 'Aktif' : 'Pasif'}
                                    </div>
                                    <button onClick={() => setEditModal({ ...sponsor, table: 'sponsors' })} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"><Edit2 size={18}/></button>
                                    <button onClick={() => handleDeleteItem(sponsor.id, 'sponsors')} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 size={18}/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )}

        {/* Content Tab */}
        {activeTab === 'content' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Winners */}
                <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden h-fit">
                    <div className="p-6 border-b border-gray-700 flex justify-between items-center bg-amber-500/5">
                        <h3 className="text-lg font-bold text-amber-400 flex items-center gap-2">
                            <Trophy size={20} /> Haftanın Kazananları
                        </h3>
                        <button onClick={() => setEditModal({ table: 'winners' })} className="bg-amber-600 hover:bg-amber-500 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-2">
                            <Plus size={16} /> Ekle
                        </button>
                    </div>
                    <div className="p-4 space-y-2">
                        {winners.map((winner, idx) => (
                            <div key={winner.id} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                                <div className="flex items-center gap-3">
                                    <div className="flex flex-col text-gray-600">
                                        <button onClick={() => handleMove(idx, 'up', null, 'winners')} className="hover:text-white"><ArrowUp size={12}/></button>
                                        <button onClick={() => handleMove(idx, 'down', null, 'winners')} className="hover:text-white"><ArrowDown size={12}/></button>
                                    </div>
                                    <span className="w-6 h-6 rounded bg-amber-500/20 text-amber-500 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                                    <div>
                                        <div className="font-medium text-white">{winner.user}</div>
                                        <div className="text-xs text-gray-400">{winner.amount} • {winner.site}</div>
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => setEditModal({ ...winner, table: 'winners' })} className="p-1.5 text-gray-400 hover:text-white"><Edit2 size={14}/></button>
                                    <button onClick={() => handleDeleteItem(winner.id, 'winners')} className="p-1.5 text-red-400 hover:text-red-300"><Trash2 size={14}/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Notifications */}
                <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden h-fit">
                    <div className="p-6 border-b border-gray-700 flex justify-between items-center bg-purple-500/5">
                        <h3 className="text-lg font-bold text-purple-400 flex items-center gap-2">
                            <Bell size={20} /> Canlı Bildirimler
                        </h3>
                        <button onClick={() => setEditModal({ table: 'notifications' })} className="bg-purple-600 hover:bg-purple-500 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-2">
                            <Plus size={16} /> Ekle
                        </button>
                    </div>
                    <div className="p-4 space-y-2">
                        {notifications.map((notif) => (
                            <div key={notif.id} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                                <div>
                                    <div className="font-medium text-white">{notif.user}</div>
                                    <div className="text-xs text-gray-400">{notif.amount} • {notif.site}</div>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => setEditModal({ ...notif, table: 'notifications' })} className="p-1.5 text-gray-400 hover:text-white"><Edit2 size={14}/></button>
                                    <button onClick={() => handleDeleteItem(notif.id, 'notifications')} className="p-1.5 text-red-400 hover:text-red-300"><Trash2 size={14}/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
            <div className="space-y-6 max-w-4xl">
                <div className="bg-gray-800 rounded-2xl border border-gray-700 p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-bold text-white">Genel Ayarlar</h3>
                        <button onClick={handleSaveSettings} className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 font-medium shadow-lg shadow-blue-500/20">
                            <Save size={18} /> Değişiklikleri Kaydet
                        </button>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Site Başlığı</label>
                            <div className="flex items-center gap-3 bg-gray-900 border border-gray-700 rounded-xl p-3">
                                <Type className="text-gray-500" />
                                <input value={settings.headerTitle || ''} onChange={(e) => setSettings({...settings, headerTitle: e.target.value})} className="bg-transparent w-full text-white focus:outline-none" placeholder="Site başlığı..." />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <ImageInput 
                                    label="Logo URL" 
                                    value={settings.logoUrl} 
                                    onChange={(e) => setSettings({...settings, logoUrl: e.target.value})} 
                                    onUpload={(e) => handleSettingsUpload(e, 'logoUrl')}
                                />
                            </div>
                            <div>
                                <ImageInput 
                                    label="Favicon URL" 
                                    value={settings.favicon} 
                                    onChange={(e) => setSettings({...settings, favicon: e.target.value})} 
                                    onUpload={(e) => handleSettingsUpload(e, 'favicon')}
                                />
                            </div>
                        </div>
                        <div>
                            <ImageInput 
                                label="Arkaplan Resmi URL" 
                                value={settings.background?.image} 
                                onChange={(e) => setSettings({...settings, background: {...settings.background, image: e.target.value}})} 
                                onUpload={(e) => handleSettingsUpload(e, 'background')} // Dikkat: Bu obje olduğu için özel işlem gerekebilir ama şimdilik string URL set ediyor
                            />
                            {/* Background nested obje olduğu için onUpload fonksiyonunu özelleştirmem lazım, yukarıda handleSettingsUpload string bekliyor.
                                Ancak handleSettingsUpload sadece state güncelliyor. 
                                background.image'i güncellemek için handleSettingsUpload'ı modifiye etmek yerine buraya inline fonksiyon yazayım veya handleSettingsUpload'ı güncelleyeyim.
                                Basitlik için inline yapıyorum.
                            */}
                            {/* Düzeltme: handleSettingsUpload sadece 1. seviye key'leri güncelliyor. background.image için ayrı fonksiyon gerekli. */}
                        </div>
                    </div>
                </div>

                <div className="bg-gray-800 rounded-2xl border border-gray-700 p-6">
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-white">Popup Ayarları</h3>
                            <p className="text-sm text-gray-400">Girişte açılan reklam penceresi</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" checked={settings.popup?.active || false} onChange={(e) => setSettings({...settings, popup: {...settings.popup, active: e.target.checked}})} className="sr-only peer" />
                            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <ImageInput 
                                label="Görsel URL" 
                                value={settings.popup?.image} 
                                onChange={(e) => setSettings({...settings, popup: {...settings.popup, image: e.target.value}})} 
                                onUpload={handlePopupUpload}
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Yönlendirme Linki</label>
                            <input value={settings.popup?.url || ''} onChange={(e) => setSettings({...settings, popup: {...settings.popup, url: e.target.value}})} className="w-full bg-gray-900 border border-gray-700 rounded-xl p-3 text-white focus:border-blue-500 focus:outline-none" />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Başlık</label>
                            <input value={settings.popup?.title || ''} onChange={(e) => setSettings({...settings, popup: {...settings.popup, title: e.target.value}})} className="w-full bg-gray-900 border border-gray-700 rounded-xl p-3 text-white focus:border-blue-500 focus:outline-none" />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Buton Metni</label>
                            <input value={settings.popup?.buttonText || ''} onChange={(e) => setSettings({...settings, popup: {...settings.popup, buttonText: e.target.value}})} className="w-full bg-gray-900 border border-gray-700 rounded-xl p-3 text-white focus:border-blue-500 focus:outline-none" />
                        </div>
                    </div>
                </div>

                {/* Popup Siteleri Yönetimi */}
                <div className="bg-gray-800 rounded-2xl border border-gray-700 p-6">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-white">Popup Site Listesi</h3>
                            <p className="text-sm text-gray-400">Popup içinde gösterilecek siteler</p>
                        </div>
                        <button onClick={() => setEditModal({ table: 'popup_sites' })} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
                            <Plus size={18} /> Ekle
                        </button>
                    </div>
                    
                    <div className="space-y-2">
                        {popupSites.map((site, index) => (
                            <div key={site.id} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                                <div className="flex items-center gap-4">
                                    <div className="flex flex-col gap-1">
                                        <button onClick={() => handleMove(index, 'up', null, 'popup_sites')} disabled={index === 0} className={`p-0.5 rounded ${index === 0 ? 'text-gray-600' : 'text-gray-400 hover:text-white'}`}><ArrowUp size={14}/></button>
                                        <button onClick={() => handleMove(index, 'down', null, 'popup_sites')} disabled={index === popupSites.length - 1} className={`p-0.5 rounded ${index === popupSites.length - 1 ? 'text-gray-600' : 'text-gray-400 hover:text-white'}`}><ArrowDown size={14}/></button>
                                    </div>
                                    <div className="w-10 h-10 bg-gray-800 rounded p-1 flex items-center justify-center border border-gray-700">
                                        <img src={site.image} alt={site.name} className="max-w-full max-h-full object-contain" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white text-sm">{site.name}</h4>
                                        <div className="text-xs text-gray-500">{site.url}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button onClick={() => handleToggleActivePopupSite(site)} className={`p-1 rounded ${site.is_active == 1 ? 'text-green-400 bg-green-400/10' : 'text-gray-500 bg-gray-700'}`}>
                                        {site.is_active == 1 ? <Check size={14}/> : <X size={14}/>}
                                    </button>
                                    <button onClick={() => setEditModal({ ...site, table: 'popup_sites' })} className="p-1.5 text-gray-400 hover:text-white"><Edit2 size={14}/></button>
                                    <button onClick={() => handleDeleteItem(site.id, 'popup_sites')} className="p-1.5 text-red-400 hover:text-red-300"><Trash2 size={14}/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )}

      </main>

      {editModal && (
        <Modal 
            item={editModal.id ? editModal : null}
            table={editModal.table || 'sponsors'}
            defaultType={editModal.defaultType}
            onClose={() => setEditModal(null)} 
            onSave={handleItemSave} 
        />
      )}
    </div>
  );
};

export default Dashboard;
